/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useEffect, useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { AlertBar, BrandColors, Header, LoadingSpinner } from '@blend/enterprise-components';
import { get } from 'lodash';

import { Report } from 'blb-types/internal/api/report';
import { errorMessage } from 'blb-utils/errors';
import { getFormattedDisplay } from 'blb-utils/date';
import { getOpsStatsReport } from 'blb-api/reports';
import { TemplateSortableTable } from 'blb-components/TemplateSortableTable';

const styles = StyleSheet.create({
  centeredContainer: {
    alignItems: 'center',
    display: 'flex',
    height: '25%',
    justifyContent: 'center',
    position: 'absolute',
  },
  dateWrapper: {
    paddingLeft: '20px',
    paddingRight: '32px',
    paddingTop: '32px',
  },
  errorContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  headingDate: {
    color: BrandColors.NIGHT_90,
    fontSize: '14px',
    fontWeight: 'normal',
    paddingBottom: '10px',
  },
  rowStyle: {
    ':nth-child(odd)': {
      background: BrandColors.CLOUD_20,
    },
    height: '54px',
  },
  statsReportContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
});

export const OpsUserStatsReport: React.FC<{}> = () => {
  const [reportData, setReportData] = useState<Report.GetAssignmentReportResponse | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [loadingError, setLoadingError] = useState<string | null>(null);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const { data: fetchedReportData } = await getOpsStatsReport();
      if (fetchedReportData.headers === undefined) {
        setLoadingError(errorMessage('GENERATING_REPORT', 'ops_desk_agent'));
      } else if (fetchedReportData.records === undefined) {
        setLoadingError(errorMessage('REPORT_HAS_NO_DATA', 'ops_desk_agent'));
      } else {
        setReportData(fetchedReportData);
      }
    } catch (error) {
      setLoadingError(get(error, 'response.data', errorMessage('GENERATING_REPORT', 'ops_desk_agent')));
    } finally {
      setLoading(false);
    }
  };

  const dateContainer = (
    <div className={`${css(styles.dateWrapper)}`}>
      <Header className={css(styles.headingDate)} size={3}>
        {getFormattedDisplay(Date.now(), 'cccc, LLLL d, yyyy')}
      </Header>
    </div>
  );

  const pageBody = isLoading ? (
    <div className={css(styles.centeredContainer)}>
      <LoadingSpinner size="lg" theme="secondary" />
    </div>
  ) : (
    reportData && (
      <TemplateSortableTable
        headers={reportData.headers}
        id="it-OpsDeskUserStats-Table"
        key="OpsDeskUserStatsTable"
        refresh={fetchReport}
        rowStyle={styles.rowStyle}
        rows={reportData.records}
      />
    )
  );

  return (
    <div className={css(styles.statsReportContainer)}>
      {dateContainer}
      {loadingError ? (
        <AlertBar className={css(styles.errorContainer)} icon="error" message={loadingError} theme="error" />
      ) : (
        pageBody
      )}
    </div>
  );
};
